import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// CSRF token management for product service
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Create axios instance with HTTP-only cookie support
const API = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true, // Send HTTP-only cookies automatically
});

// Request interceptor to add CSRF token to state-changing requests
API.interceptors.request.use(
  async (config) => {
    // Only add CSRF token to state-changing methods
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (stateChangingMethods.includes(config.method.toUpperCase())) {
      // Fetch token if not already cached
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Add CSRF token to header
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle CSRF token expiration
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If we get a 403 CSRF error, try to refresh the token and retry
    if (error.response && error.response.status === 403 && error.response.data.error === 'CSRF token validation failed') {
      // Clear cached token and fetch a new one
      csrfToken = null;
      await fetchCsrfToken();
      
      // Retry the request with the new token
      const config = error.config;
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
        return API.request(config);
      }
    }
    return Promise.reject(error);
  }
);

// Get all products with filtering
export const getProducts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add filters to query params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    
    const response = await API.get(`/admin/products?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to fetch products';
  }
};

// Get single product by ID
export const getProductById = async (id) => {
  try {
    const response = await API.get(`/admin/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to fetch product';
  }
};

// Create new product
export const createProduct = async (productData) => {
  try {
    logger.debug("Creating product with data:", JSON.stringify(productData));
    const response = await API.post('/admin/products', productData);
    logger.debug("Create product response:", response.data);
    return response.data;
  } catch (error) {
    logger.error("Create product error:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message || 'Failed to create product';
  }
};

// Update product
export const updateProduct = async (id, productData) => {
  try {
    const response = await API.put(`/admin/products/${id}`, productData);
    return response.data;
  } catch (error) {
    console.error("Update product error:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message || 'Failed to update product';
  }
};

// Delete product
export const deleteProduct = async (id) => {
  try {
    const response = await API.delete(`/admin/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to delete product';
  }
};

// Upload product images
export const uploadProductImages = async (id, formData) => {
  try {
    const response = await API.post(`/admin/products/${id}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to upload images';
  }
};

// Delete product image
export const deleteProductImage = async (productId, imageId) => {
  try {
    const response = await API.delete(`/admin/products/${productId}/images/${imageId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to delete image';
  }
};

// Set main product image
export const setMainProductImage = async (productId, imageId) => {
  try {
    const response = await API.put(`/admin/products/${productId}/images/${imageId}/main`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to set main image';
  }
}; 